/* eslint-disable react/prop-types */
import dayjs from 'dayjs'
import React from 'react'

import { apiEndpoints } from 'api/common'
import { makeRequestUrl } from 'app/helpers'
import { get } from 'request'

import * as styles from './styles'

const formatDate = (value) => (value ? dayjs(value).format('D MMM YYYY') : '—')

const rupees = (value) =>
  `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`

export const fetchActiveCareSummary = async (studentUuid) => {
  const res = await get(
    makeRequestUrl(
      `${apiEndpoints.invoiceSwitchBillingMode}/active_care_link/summary`,
      { student: studentUuid }
    )
  )
  return res.data?.active_care || null
}

/**
 * Active Care at a glance on the child's drawer: current plan, batch,
 * paid-until and next due (the next period after paid-until), so the team
 * need not search Orders. Older or Drop-in links never paid are listed
 * apart under "Other unpaid". Renders
 * nothing for a child who has never had an Active Care link.
 */
export const ActiveCareCard = ({ studentUuid }) => {
  const [summary, setSummary] = React.useState(null)

  React.useEffect(() => {
    if (!studentUuid) return
    let cancelled = false
    setSummary(null)
    fetchActiveCareSummary(studentUuid)
      .then((result) => !cancelled && setSummary(result))
      .catch((err) => console.error('Active Care summary failed', err))
    return () => {
      cancelled = true
    }
  }, [studentUuid])

  if (!summary) return null
  const {
    current,
    paid_until: paidUntil,
    next_due: nextDue,
    other_unpaid: otherUnpaid = [],
    overdue
  } = summary

  return (
    <styles.ActiveCareCard>
      <styles.ItemTitle>
        <span>Active Care</span>
      </styles.ItemTitle>
      <styles.ActiveCareRow>
        <span>Plan</span>
        <span>{current?.label || 'no paid plan yet'}</span>
      </styles.ActiveCareRow>
      <styles.ActiveCareRow>
        <span>Batch</span>
        <span>{current?.batch_name || '—'}</span>
      </styles.ActiveCareRow>
      <styles.ActiveCareRow>
        <span>Paid until</span>
        <span>{formatDate(paidUntil)}</span>
      </styles.ActiveCareRow>
      <styles.ActiveCareRow>
        <span>Next due</span>
        <span>
          {!nextDue ? (
            '—'
          ) : nextDue.kind === 'unpaid_link' ? (
            <>
              {overdue ? <styles.Overdue>Overdue · </styles.Overdue> : null}
              {formatDate(nextDue.date)} · {rupees(nextDue.amount)} unpaid
              {nextDue.payment_link ? (
                <>
                  {' · '}
                  <a
                    href={nextDue.payment_link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    link
                  </a>
                </>
              ) : null}
            </>
          ) : (
            `${formatDate(nextDue.date)}${
              nextDue.amount ? ` · ${rupees(nextDue.amount)}` : ''
            } (renewal)`
          )}
        </span>
      </styles.ActiveCareRow>
      {otherUnpaid.length ? (
        <styles.ActiveCareRow>
          <span>Other unpaid</span>
          <span>
            {otherUnpaid.map((link, index) => (
              <React.Fragment key={`${link.date}-${index}`}>
                {index ? <br /> : null}
                {formatDate(link.date)} · {rupees(link.amount)}
                {link.payment_link ? (
                  <>
                    {' · '}
                    <a
                      href={link.payment_link}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      link
                    </a>
                  </>
                ) : null}
              </React.Fragment>
            ))}
          </span>
        </styles.ActiveCareRow>
      ) : null}
    </styles.ActiveCareCard>
  )
}
